import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { PageHeader, Tag } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Target, Award, Trash2, Star, Quote } from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/dateFormat";

const isInactiveUser = (u) => ["deactivated", "inactive"].includes(u.status);

export default function Goals() {
  const { user, adminMode } = useAuth();
  const can = usePermissions();
  // True admin (role) → drives data scoping (admin can pick another user).
  // Effective admin (role && adminMode) → drives mutation UI (add/edit/delete).
  const isAdmin = can("goals_create") || can("goals_edit") || can("goals_delete");
  const canEdit = isAdmin && adminMode;
  const [users, setUsers] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [pickedUser, setPickedUser] = useState(isAdmin ? "" : user?.id);
  const [goals, setGoals] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [appreciation, setAppreciation] = useState({ average_rating: null, count: 0, appreciations: [] });
  const [goalOpen, setGoalOpen] = useState(false);
  const [achOpen, setAchOpen] = useState(false);
  const [goalForm, setGoalForm] = useState({ title: "", description: "", target_date: "", metric: "", progress: 0 });
  const [achForm, setAchForm] = useState({ title: "", description: "", score: 5, achieved_at: "" });

  useEffect(() => {
    if (isAdmin) api.get("/users", { params: { kind: "internal", include_inactive: showInactive } }).then((r) => setUsers(r.data)).catch(() => {});
  }, [isAdmin, showInactive]);

  useEffect(() => {
    const params = isAdmin && pickedUser ? { user_id: pickedUser } : {};
    if (isAdmin && !pickedUser) { setGoals([]); setAchievements([]); setAppreciation({ average_rating: null, count: 0, appreciations: [] }); return; }
    const targetUid = isAdmin && pickedUser ? pickedUser : user?.id;
    Promise.all([
      api.get("/goals", { params }),
      api.get("/achievements", { params }),
      targetUid ? api.get(`/users/${targetUid}/appreciations`) : Promise.resolve({ data: { average_rating: null, count: 0, appreciations: [] } }),
    ]).then(([g, a, ap]) => { setGoals(g.data); setAchievements(a.data); setAppreciation(ap.data); }).catch(() => {});
  }, [pickedUser, isAdmin, user?.id]);

  const saveGoal = async () => {
    try { await api.post("/goals", { ...goalForm, user_id: pickedUser, progress: Number(goalForm.progress) }); toast.success("Goal added"); setGoalOpen(false); const { data } = await api.get("/goals", { params: { user_id: pickedUser } }); setGoals(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const updateProgress = async (gid, progress) => {
    try { await api.patch(`/goals/${gid}`, { progress: Number(progress) }); setGoals((arr) => arr.map((g) => g.id === gid ? { ...g, progress: Number(progress) } : g)); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delGoal = async (gid) => { try { await api.delete(`/goals/${gid}`); setGoals((g) => g.filter((x) => x.id !== gid)); } catch (e) { toast.error(formatApiError(e)); } };

  const saveAch = async () => {
    try { await api.post("/achievements", { ...achForm, user_id: pickedUser, score: Number(achForm.score) }); toast.success("Achievement added"); setAchOpen(false); const { data } = await api.get("/achievements", { params: { user_id: pickedUser } }); setAchievements(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const delAch = async (aid) => { try { await api.delete(`/achievements/${aid}`); setAchievements((a) => a.filter((x) => x.id !== aid)); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto fade-in" data-testid="goals-page">
      <PageHeader
        eyebrow="Growth"
        title={isAdmin ? "Goals & achievements" : "My goals"}
        subtitle={isAdmin ? "Set quarterly goals and score top achievements for each team member." : "Goals set by your manager. Progress updates roll up to your dashboard."}
        actions={canEdit && (
          pickedUser ? (
          <div className="flex items-center gap-2">
            <Dialog open={goalOpen} onOpenChange={setGoalOpen}>
              <DialogTrigger asChild><Button variant="outline" className="rounded-sm" data-testid="new-goal-btn"><Target size={14} className="mr-1.5" />Add goal</Button></DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader><DialogTitle className="font-display">Add goal</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <Input className="rounded-sm" placeholder="Goal title" value={goalForm.title} onChange={(e) => setGoalForm({ ...goalForm, title: e.target.value })} />
                  <Textarea rows={3} className="rounded-sm" placeholder="Description" value={goalForm.description} onChange={(e) => setGoalForm({ ...goalForm, description: e.target.value })} />
                  <div className="grid grid-cols-2 gap-3">
                    <Input className="rounded-sm" placeholder="Metric (e.g. ship 3 concepts)" value={goalForm.metric} onChange={(e) => setGoalForm({ ...goalForm, metric: e.target.value })} />
                    <Input type="date" className="rounded-sm" value={goalForm.target_date} onChange={(e) => setGoalForm({ ...goalForm, target_date: e.target.value })} />
                  </div>
                </div>
                <DialogFooter><Button onClick={saveGoal} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white">Add</Button></DialogFooter>
              </DialogContent>
            </Dialog>
            <Dialog open={achOpen} onOpenChange={setAchOpen}>
              <DialogTrigger asChild><Button className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid="new-ach-btn"><Award size={14} className="mr-1.5" />Score achievement</Button></DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader><DialogTitle className="font-display">Add achievement</DialogTitle></DialogHeader>
                <div className="space-y-3 mt-2">
                  <Input className="rounded-sm" placeholder="Achievement" value={achForm.title} onChange={(e) => setAchForm({ ...achForm, title: e.target.value })} />
                  <Textarea rows={2} className="rounded-sm" placeholder="Why it matters" value={achForm.description} onChange={(e) => setAchForm({ ...achForm, description: e.target.value })} />
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="label-overline">Score (1-10)</Label><Input type="number" min={1} max={10} className="rounded-sm mt-1" value={achForm.score} onChange={(e) => setAchForm({ ...achForm, score: e.target.value })} /></div>
                    <div><Label className="label-overline">When</Label><Input type="date" className="rounded-sm mt-1" value={achForm.achieved_at} onChange={(e) => setAchForm({ ...achForm, achieved_at: e.target.value })} /></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={saveAch} className="rounded-sm bg-[#F54800] hover:bg-[#D63E00] text-white">Add</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          ) : (
            <span className="text-[11px] text-slate-500 inline-flex items-center gap-1" data-testid="goals-pick-hint">
              Pick a member below to add goals or score achievements
            </span>
          )
        )}
      />

      {isAdmin && (
        <div className="mb-6 flex items-center gap-3 flex-wrap">
          <Label className="label-overline">Team member</Label>
          <Select value={pickedUser || "none"} onValueChange={(v) => setPickedUser(v === "none" ? "" : v)}>
            <SelectTrigger className="rounded-sm w-72" data-testid="goal-user-pick"><SelectValue placeholder="Select a team member" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Pick someone —</SelectItem>
              {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}{isInactiveUser(u) && <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Inactive</span>}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Switch id="goals-show-inactive" checked={showInactive} onCheckedChange={setShowInactive} data-testid="goals-show-inactive-toggle" />
            <Label htmlFor="goals-show-inactive" className="text-xs text-slate-500 cursor-pointer">Show inactive</Label>
          </div>
        </div>
      )}

      <section className="mb-8">
        <div className="label-overline mb-3">Goals</div>
        <div className="space-y-2">
          {goals.map((g) => (
            <div key={g.id} className="card-flat p-4" data-testid={`goal-${g.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900">{g.title}</div>
                  {g.description && <div className="text-xs text-slate-500 mt-1">{g.description}</div>}
                  <div className="text-xs text-slate-500 mt-1">{g.metric || ""} {g.target_date ? `· Due ${formatDate(g.target_date)}` : ""}</div>
                  <div className="mt-3 h-2 bg-slate-100 rounded-sm overflow-hidden">
                    <div className="h-full bg-[#F54800]" style={{ width: `${Math.min(100, g.progress || 0)}%` }} />
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Input type="number" min={0} max={100} className="rounded-sm w-20 h-8 text-sm" value={g.progress || 0} onChange={(e) => updateProgress(g.id, e.target.value)} data-testid={`goal-progress-${g.id}`} />
                  <span className="text-xs text-slate-500">%</span>
                  {canEdit && <button onClick={() => delGoal(g.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
                </div>
              </div>
            </div>
          ))}
          {goals.length === 0 && <div className="card-flat p-6 text-center text-sm text-slate-500">{isAdmin && !pickedUser ? "Pick a team member to view their goals." : "No goals set."}</div>}
        </div>
      </section>

      <section>
        <div className="label-overline mb-3">Top achievements</div>
        <div className="space-y-2">
          {achievements.slice(0, 5).map((a, i) => (
            <div key={a.id} className="card-flat p-4 flex items-center gap-4" data-testid={`achievement-${a.id}`}>
              <div className="w-10 h-10 rounded-sm bg-[#F54800]/10 text-[#F54800] flex items-center justify-center font-display font-bold">#{i + 1}</div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900">{a.title}</div>
                {a.description && <div className="text-xs text-slate-500 mt-1">{a.description}</div>}
                <div className="text-xs text-slate-500 mt-1">{formatDate(a.achieved_at)}</div>
              </div>
              <Tag tone="brand">{a.score}/10</Tag>
              {canEdit && <button onClick={() => delAch(a.id)} className="text-slate-400 hover:text-red-600"><Trash2 size={14} /></button>}
            </div>
          ))}
          {achievements.length === 0 && <div className="card-flat p-6 text-center text-sm text-slate-500">No achievements yet.</div>}
        </div>
      </section>

      {(isAdmin ? !!pickedUser : true) && (
        <section className="mt-8" data-testid="client-appreciation-section">
          <div className="label-overline mb-3 flex items-center justify-between">
            <span>Client appreciation</span>
            {appreciation.average_rating != null && (
              <span className="inline-flex items-center gap-1 text-[#F54800] font-semibold text-xs" data-testid="client-appreciation-average">
                <Star size={12} className="fill-[#F54800]" /> {appreciation.average_rating}/5 · {appreciation.count} rating{appreciation.count === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {(appreciation.appreciations || []).map((a) => (
              <div key={a.id} className="card-flat p-4" data-testid={`client-appreciation-row-${a.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1 text-sm">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={13} className={a.rating >= s ? "text-[#F54800] fill-[#F54800]" : "text-slate-300"} />
                      ))}
                    </div>
                    {a.feedback && <div className="text-sm text-slate-700 mt-2 italic inline-flex items-start gap-1.5"><Quote size={12} className="text-slate-300 mt-0.5 shrink-0" />&quot;{a.feedback}&quot;</div>}
                    <div className="text-xs text-slate-500 mt-1.5">{a.client_name} · {a.project_name} · {formatDate(a.created_at)}</div>
                  </div>
                </div>
              </div>
            ))}
            {(appreciation.appreciations || []).length === 0 && (
              <div className="card-flat p-6 text-center text-sm text-slate-500">No client ratings yet.</div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
